import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { D365FinanceOperationsSetupRequest } from '@/models/payment';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { InputText } from '@/components/shared/InputText';
import { Box, Grid } from '@mui/material';
import {
    PrimaryButtonStyle,
    SecondaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';

interface D365FinanceOperationsConnectProps {
    onClose?: () => void;
    onSaveSuccess: any;
}

export const D365FinanceOperationsConnect = (
    props: D365FinanceOperationsConnectProps
) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<D365FinanceOperationsSetupRequest>(
        {
            tenantId: '',
            resourceUrl: '',
            clientId: '',
            clientSecret: '',
            getEmployeeListUrl: '',
            reportTransactionsUrl: '',
            verifyTransactionsUrl: '',
            dataAreaId: '',
            lessorJournalNum: '',
            lessorPaytypeNum: ''
        }
    );
    const { d365FinanceOperationsSetup } = usePaymentsMutations();

    const onChange =
        (field: keyof D365FinanceOperationsSetupRequest) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setFormData({ ...formData, [field]: e.target.value });
        };

    const isValid =
        !!formData.tenantId &&
        !!formData.resourceUrl &&
        !!formData.clientId &&
        !!formData.clientSecret &&
        !!formData.getEmployeeListUrl &&
        !!formData.reportTransactionsUrl &&
        !!formData.verifyTransactionsUrl &&
        !!formData.dataAreaId &&
        !!formData.lessorJournalNum &&
        !!formData.lessorPaytypeNum;

    useHandleResponse({
        isSuccess: d365FinanceOperationsSetup.isSuccess,
        error: d365FinanceOperationsSetup.error,
        isPending: d365FinanceOperationsSetup.isPending,
        successAlertMessage: t('connected'),
        isSuccessCallBack: () => {
            props.onSaveSuccess();
        }
    });

    return (
        <>
            <Box
                width={'100%'}
                margin={'20px 0 10px'}
                sx={{ maxHeight: '60vh', overflowY: 'auto' }}
            >
                <InputText
                    value={formData.tenantId}
                    label={t('d365_tenant_id')}
                    placeholder={t('d365_tenant_id_placeholder')}
                    onChange={onChange('tenantId')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.resourceUrl}
                    label={t('d365_resource_url')}
                    placeholder={t('d365_resource_url_placeholder')}
                    onChange={onChange('resourceUrl')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.clientId}
                    label={t('d365_client_id')}
                    placeholder={t('d365_client_id_placeholder')}
                    onChange={onChange('clientId')}
                    spacing="10px 0"
                />
                <InputText
                    type="password"
                    value={formData.clientSecret}
                    label={t('d365_client_secret')}
                    placeholder={t('d365_client_secret_placeholder')}
                    onChange={onChange('clientSecret')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.getEmployeeListUrl}
                    label={t('d365_get_employee_list_url')}
                    placeholder={t('d365_get_employee_list_url_placeholder')}
                    onChange={onChange('getEmployeeListUrl')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.reportTransactionsUrl}
                    label={t('d365_report_transactions_url')}
                    placeholder={t('d365_report_transactions_url_placeholder')}
                    onChange={onChange('reportTransactionsUrl')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.verifyTransactionsUrl}
                    label={t('d365_verify_transactions_url')}
                    placeholder={t('d365_verify_transactions_url_placeholder')}
                    onChange={onChange('verifyTransactionsUrl')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.dataAreaId}
                    label={t('d365_data_area_id')}
                    placeholder={t('d365_data_area_id_placeholder')}
                    onChange={onChange('dataAreaId')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.lessorJournalNum}
                    label={t('d365_lessor_journal_num')}
                    placeholder={t('d365_lessor_journal_num_placeholder')}
                    onChange={onChange('lessorJournalNum')}
                    spacing="10px 0"
                />
                <InputText
                    value={formData.lessorPaytypeNum}
                    label={t('d365_lessor_paytype_num')}
                    placeholder={t('d365_lessor_paytype_num_placeholder')}
                    onChange={onChange('lessorPaytypeNum')}
                    spacing="10px 0"
                />
            </Box>

            <Grid
                container
                spacing={2}
                justifyContent={'space-around'}
                sx={{ m: '20px 0' }}
            >
                <Grid>
                    <SecondaryButtonStyle
                        onClick={props.onClose}
                        sx={{ width: '170px' }}
                    >
                        {t('back')}
                    </SecondaryButtonStyle>
                </Grid>

                <Grid>
                    <PrimaryButtonStyle
                        onClick={() =>
                            d365FinanceOperationsSetup.mutate(formData)
                        }
                        sx={{ width: '170px' }}
                        loading={d365FinanceOperationsSetup.isPending}
                        disabled={!isValid}
                    >
                        {t('connect')}
                    </PrimaryButtonStyle>
                </Grid>
            </Grid>
        </>
    );
};
